import React, { useCallback } from 'react';
import { type Task, Checklist } from '@atlassiansox/checklist';
import { useIntl } from '@atlassian/jira-intl';
import { useRightSidebarController } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { useActions, useOpenTaskId, useTasks } from '../services/index.tsx';
import {
	type QuickstartUiStatus,
	QUICKSTART_UI_STATUS_MINIMIZED,
	QUICKSTART_PANEL_ID,
	QUICKSTART_UI_STATUS_DISMISSED,
} from '../services/constants.tsx';
import type { TaskId } from '../tasks/constants.tsx';
import { OnboardingChecklistFooter } from './OnboardingChecklistFooter.tsx';
import messages from './OnboardingCheckList.messages.tsx';

import { OnboardingCheckListPostTasks } from './OnboardingCheckListPostTasks.tsx';

type TaskDictionary = Record<string, Omit<Task, 'completed'>>;

export const OnboardingCheckList = ({
	taskDictionary,
}: {
	taskDictionary: TaskDictionary;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const tasks = useTasks();
	const openTaskId = useOpenTaskId();
	const { closeCurrentTask, setOpenTaskId, setQuickstartBannerVisibility } = useActions();
	const { closeRightSidebar } = useRightSidebarController();

	const handleTaskExpand = useCallback(
		(id: string) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			setOpenTaskId({ id: id as TaskId });
		},
		[setOpenTaskId],
	);

	if (!tasks) {
		return null;
	}

	const changeQuickstartBannerVisibility = ({ visibility }: { visibility: QuickstartUiStatus }) => {
		closeCurrentTask();
		setQuickstartBannerVisibility({ visibility });
		closeRightSidebar(QUICKSTART_PANEL_ID);
	};

	// TODO: POL-17792 handle the case where the task is not in the taskDictionary
	const tasksToRender = tasks.items.map((task) => {
		return {
			...taskDictionary[task.id],
			completed: task.completed,
		};
	});

	return (
		<Checklist
			tasks={tasksToRender}
			isOpen
			onClose={() =>
				changeQuickstartBannerVisibility({ visibility: QUICKSTART_UI_STATUS_MINIMIZED })
			}
			title={formatMessage(messages.title)}
			expandedTask={openTaskId ?? undefined}
			onTaskExpand={handleTaskExpand}
			postTasksComponent={<OnboardingCheckListPostTasks />}
			footer={{
				wrapper: OnboardingChecklistFooter,
				dismissTitle: formatMessage(messages.dismissQuickstart),
				onDismiss: () =>
					changeQuickstartBannerVisibility({ visibility: QUICKSTART_UI_STATUS_DISMISSED }),
			}}
		/>
	);
};
